import { useEffect, useRef, useState } from 'react'
import Cart from 'shared-svg/Cart.svg'
import styles from './OnThisPageNav.module.css'
import { useCatalogEvent } from 'lib/analytics/catalog'

export interface TocSection {
  id: string
  label: string
}

interface OnThisPageNavProps {
  sections: TocSection[]
  amazonHref: string | null
}

// Distance from the top of the viewport (px) used both as the click-scroll offset
// and the scrollspy probe line. The site header collapses on scroll, so a constant
// keeps anchor landings stable without tracking the moving header.
const SCROLL_OFFSET = 120

/**
 * Smooth-scroll to a page section, clearing the sticky nav. Shared by the nav tabs and
 * any in-page link that targets a section (the hero's lab bridge), so every jump on
 * the page feels the same. Returns false when the section isn't rendered — the caller
 * then leaves the plain `#hash` navigation alone.
 */
export function scrollToSection(id: string): boolean {
  const el = typeof document !== 'undefined' ? document.getElementById(id) : null
  if (!el) {
    return false
  }
  const y = el.getBoundingClientRect().top + window.pageYOffset - SCROLL_OFFSET
  window.scrollTo({ top: Math.max(0, y), behavior: 'smooth' })
  return true
}

/**
 * Sticky "On this page" table of contents with scroll-spy, shown under the site
 * header on the redesigned product page. Sections are passed in by the template so
 * blocks with no data are omitted from the nav. Replaces the design mockup's
 * vanilla-JS TOC.
 */
const OnThisPageNav: React.VFC<OnThisPageNavProps> = ({ sections, amazonHref }) => {
  const [active, setActive] = useState<string>(sections[0]?.id ?? '')
  const linksRef = useRef<HTMLDivElement>(null)
  const { track } = useCatalogEvent()

  useEffect(() => {
    if (typeof window === 'undefined' || !sections.length) {
      return
    }
    const ids = sections.map(s => s.id)

    const onScroll = (): void => {
      const probe = window.pageYOffset + SCROLL_OFFSET + 16
      let [current] = ids
      for (const id of ids) {
        const el = document.getElementById(id)
        if (el && el.getBoundingClientRect().top + window.pageYOffset <= probe) {
          current = id
        }
      }
      // Snap to the last section once scrolled to the bottom of the page.
      if (window.innerHeight + window.pageYOffset >= document.body.scrollHeight - 4) {
        current = ids[ids.length - 1]
      }
      setActive(current)
    }

    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', onScroll)
    return () => {
      window.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', onScroll)
    }
  }, [sections])

  // On phones the tab strip scrolls sideways and only the first two or three tabs fit, so
  // the scroll-spy's highlight was moving off-screen — the strip looked frozen on "Overview"
  // however far down the page the reader was. Keep the active tab in view, centred, whenever
  // it changes. The strip's own scrollTo never moves the page (scrollIntoView would).
  useEffect(() => {
    const strip = linksRef.current
    const tab = strip?.querySelector<HTMLElement>('[data-active="true"]')
    if (!strip || !tab || strip.scrollWidth <= strip.clientWidth) {
      return
    }
    const left = tab.offsetLeft - (strip.clientWidth - tab.offsetWidth) / 2
    strip.scrollTo({ left: Math.max(0, left), behavior: 'smooth' })
  }, [active])

  const onLinkClick = (e: React.MouseEvent<HTMLAnchorElement>, id: string): void => {
    track('product_page_section_nav_click', { section: id.replace(/^sec-/, '') })
    if (scrollToSection(id)) {
      e.preventDefault()
      setActive(id)
    }
  }

  if (!sections.length) {
    return null
  }

  return (
    <nav className={styles.nav} aria-label="On this page">
      <div className={styles.inner}>
        <div className={styles.links} ref={linksRef}>
          <span className={styles.eyebrow}>{'On this page'}</span>
          {sections.map(s => (
            <a
              key={s.id}
              href={`#${s.id}`}
              className={styles.link}
              data-active={active === s.id}
              onClick={e => onLinkClick(e, s.id)}
            >
              {s.label}
            </a>
          ))}
        </div>
        {amazonHref && (
          <a
            className={styles.amazon}
            href={amazonHref}
            target="_blank"
            rel="nofollow sponsored noopener"
            aria-label="View on Amazon"
            onClick={() => track('product_page_buy_click', { location: 'sticky_nav' })}
          >
            <Cart className={styles.cart} />
            <span className={styles.amazonLabel}>{'View on Amazon'}</span>
          </a>
        )}
      </div>
    </nav>
  )
}

export default OnThisPageNav
